<script setup lang="ts">
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
// biome-ignore lint/correctness/noUnusedImports: used via tag <KioskLayout> in <template>
import KioskLayout from '@/app/layouts/kiosk/KioskLayout.vue';
import { store } from '@/app/store';
import { session } from '@/services/Session';
import { sentryService } from '@/services/telemetry/sentry';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import { track } from '@/shared/utils/track-telemetry';
// biome-ignore lint/correctness/noUnusedImports: used via tag <KioskTerminalMessage> in <template>
import KioskTerminalMessage from './terminal/KioskTerminalMessage.vue';
import { useSessionEndScreen } from './terminal/useSessionEndScreen';

interface SessionSnapshot {
  createdAt: Date | null;
  gdprConsent: boolean | null;
  gophotoCode: string | null;
  id: string | null;
}

interface SuccessPublicApi {
  _endSessionAndReturn: () => Promise<void>;
  readonly isNavigatingAway: boolean;
  onStartAgainClick: () => Promise<void>;
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const { t } = useI18n();
const sessionSnapshot: SessionSnapshot = {
  id: session.id as string | null,
  createdAt: session.createdAt as Date | null,
  gophotoCode: session.gophotoCode as string | null,
  gdprConsent: session.gdprConsent as boolean | null,
};

function captureSessionStateAttachment(): void {
  track('[Sentry] State attachment failed:', () => {
    sentryService.captureSessionState({
      sessionId: sessionSnapshot.id,
      createdAt: sessionSnapshot.createdAt?.toISOString(),
      completedAt: new Date().toISOString(),
      gdprConsent: sessionSnapshot.gdprConsent,
      gophotoCode: sessionSnapshot.gophotoCode,
      locale: store.currentLocale?.code,
      kioskSlug: store.kiosk?.slug,
      kioskPartner: store.kiosk?.partner,
      selectedStyleId: store.selectedStyle?.id,
      selectedStyleTitle: store.selectedStyle?.title,
      photosCount: store.photos?.length || 0,
      facesSelectedCount: store.facesSelected?.length || 0,
      portraitsCount: store.portraits?.length || 0,
      groupPortraitsCount: store.groupPortraits?.length || 0,
      printQueueCount: store.printingTotalCount || 0,
      printingHadErrors: store.printingHasErrors,
    });
  });
}

function trackSessionCompletion(): void {
  track('[Sentry] Session completion tracking failed:', () => {
    const sessionDurationMs = sessionSnapshot.createdAt
      ? Date.now() - sessionSnapshot.createdAt.getTime()
      : null;

    sentryService.addBreadcrumb('session', 'Session completed successfully', {
      session_id: sessionSnapshot.id,
      duration_ms: sessionDurationMs,
      portraits_count: store.portraits?.length || 0,
      group_portraits_count: store.groupPortraits?.length || 0,
      print_queue_count: store.printingTotalCount || 0,
    });
    sentryService.incrementCounter('kiosk.session.completed', 1, {
      has_portraits: (store.portraits?.length || 0) > 0,
      has_group_portraits: (store.groupPortraits?.length || 0) > 0,
      has_gophoto: Boolean(sessionSnapshot.gophotoCode),
    });

    if (sessionDurationMs) {
      sentryService.trackMetric(
        'kiosk.session.duration_seconds',
        Math.round(sessionDurationMs / 1000),
        'second'
      );
    }

    captureSessionStateAttachment();
  });
}

onMounted(() => {
  exposeToGlobalScope(publicApi, 'Success');
  trackSessionCompletion();
});

const { endSessionAndReturn, isNavigatingAway } = useSessionEndScreen({
  autoEndAfterMs: 30_000,
  screenName: 'Success',
});

async function onStartAgainClick(): Promise<void> {
  try {
    sentryService.addBreadcrumb('user', 'Clicked Start Again button', {
      session_id: sessionSnapshot.id,
    });
    void sentryService.flush(500).catch(() => undefined);
  } catch (_error) {
    // Session cleanup and navigation must continue if telemetry fails.
  }

  await endSessionAndReturn();
}

const publicApi: SuccessPublicApi = {
  _endSessionAndReturn: endSessionAndReturn,
  get isNavigatingAway(): boolean {
    return isNavigatingAway.value;
  },
  onStartAgainClick,
};

defineExpose(publicApi);
</script>

<template>
  <KioskLayout :is-centered="true">
    <KioskTerminalMessage
      action-test-id="success-start-again"
      :action-disabled="isNavigatingAway"
      :action-label="t('Success.buttonStartAgain')"
      :caption="t('Success.caption')"
      caption-class="Success__caption"
      caption-max-width="30rem"
      preserve-caption-lines
      root-class="Success"
      root-test-id="success-root"
      :title="t('Success.title')"
      @action="onStartAgainClick"
    />
  </KioskLayout>
</template>
